<template>
  <div ref="wrapper" :style="wrapperStyles" class="baet-Wrapper">
    <div class="baet-Header">
      <portal-target name="okr-sticky-header" />
    </div>

    <ObjectiveListPage
      v-if="workspaceId"
      ref="list"
      :split-first-filter="false"
      :user-can-create-objectives="false"
      :view="OKR_VIEW_PAGES.ALIGNMENT"
      :workspace-id="workspaceId"
      disable-restore-filter-values
      disable-save-filter-values
      hide-custom-filters-saver
      hide-toolbar-navigation
      offset-left="var(--page-left-padding)"
      offset-right="var(--page-right-padding)"
      @data-request="onDataRequest"
    >
      <template #toolbar-filters-prepend>
        <AppSelect
          v-model="workspaceId"
          :data-auto-testid="WORKSPACE_FILTER_TEST_ID"
          :data-testid="WORKSPACE_FILTER_TEST_ID"
          :dropdown-min-width="220"
          :offset="[0, 0]"
          :options="workspaces"
          :search-function="value => localSearch({ value, options: workspaces })"
          :type="SELECT_TYPES.SECONDARY_NEXT"
          append-icon-height="20"
          append-icon-width="20"
          boundary="scrollParent"
          class="baet-Filter"
          dropdown-search
          icon="workspace"
          is-highlighted
          item-label="name"
          item-value="id"
          n-selected-label="workspaces.workspaces"
          skeleton-loader
          skeleton-loader-height="100%"
          skeleton-loader-width="100%"
        >
          <template #option-label="{ option }">
            <WorkspaceSelectOption v-if="option" :workspace="option" hide-lock-icon show-tooltip />
          </template>

          <template #button-content="{ option }">
            <AppSelectItem
              v-if="option"
              v-tippy="{
                ...TOOLTIP_CONFIGURATION,
                content: option.name
              }"
              :show-delete-button="false"
              :show-selected-count-circle="false"
              :type="SELECT_TYPES.SECONDARY_NEXT"
            >
              <div class="baet-SelectedItemWrapper">
                <span class="baet-SelectedItemLabel"> {{ option.name }} </span>
              </div>
            </AppSelectItem>
          </template>
        </AppSelect>
      </template>

      <div class="baet-TableWrapper">
        <ObjectiveTable
          ref="table"
          :initial-columns="columns"
          :table-row-grade-configurable="false"
          :user-can-create-objectives="false"
          :workspace-id="workspaceId"
          disable-horizontal-scroll
          disable-rows-drag
          disable-save-filter-values
          disable-virtual-scroll
          edit-external
          hide-bulk-actions
          hide-head-cells-sort-icons
          hide-okr-element-rename
          hide-table-header-actions
          no-padding-bottom
          offset-left="var(--page-left-padding)"
          offset-right="var(--page-right-padding)"
          show-filter-buttons
          transparent-filtered
          @load-more="onLoadMore"
          @reset-search-criteria="onResetSearchCriteria"
          @expand-all="expandAll"
          @collapse-all="collapseAll"
          @update-okr-data="updateOkrData"
        />
      </div>
    </ObjectiveListPage>

    <div v-if="!isEmpty(elementsToAdd)" class="baet-Footer">
      <div v-if="counter" class="baet-Footer_Counter">
        {{ counter }}
      </div>

      <div class="baet-Footer_Actions">
        <ClearFiltersButton data-auto-testid="clear-selected-button" @click="clearElementsToAdd">
          {{ t('bulk_add.clear_selected', elementsToAddCount) }}
        </ClearFiltersButton>

        <AppButton
          :loading="loading"
          data-auto-testid="confirm-button"
          icon="plus-next"
          type="primary-next"
          @click="onConfirm"
        >
          {{ t('bulk_add.add_elements', elementsToAddCount) }}
        </AppButton>
      </div>
    </div>
  </div>
</template>

<script setup>
import { difference, isEmpty, memoize } from 'lodash'
import { computed, nextTick, onMounted, provide, ref, toValue, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import { SELECT_TYPES } from '@/utils/components-configurations/app-select'
import { OKR_ELEMENT_ENTITY_KEYS } from '@/utils/entity-keys'
import { bulkAddStateInjectionKey } from '@/utils/injection-keys'
import { COLUMNS_SETTINGS } from '@/utils/objective-table'
import { OBJECTIVE_TYPES } from '@/utils/objective-types'
import { currentUserCanReadObjective, localSearch, OKR_VIEW_PAGES } from '@/utils/objectives'
import { TOOLTIP_CONFIGURATION } from '@/utils/tippy-config'

import ObjectiveListPage from '@/components/objectives/ObjectiveListPage'
import ObjectiveTable from '@/components/objectives/table/ObjectiveTable'
import { WORKSPACE_FILTER_TEST_ID } from '@/components/objectives/toolbar/jest-helpers'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppSelect from '@/components/ui/AppSelect/AppSelect'
import AppSelectItem from '@/components/ui/AppSelect/AppSelectItem'
import ClearFiltersButton from '@/components/ui/ClearFiltersButton/ClearFiltersButton'
import WorkspaceSelectOption from '@/components/ui/WorkspaceSelectOption/WorkspaceSelectOption'

defineOptions({
  name: 'BulkAddElementsTable'
})

const props = defineProps({
  columns: {
    type: Array,
    default: () => [
      COLUMNS_SETTINGS.NAME.key,
      COLUMNS_SETTINGS.INTERVAL.key,
      COLUMNS_SETTINGS.ASSIGNEE.key,
      COLUMNS_SETTINGS.GROUPS.key
    ]
  },

  alreadyAddedElements: {
    type: Array,
    default: () => []
  },

  limit: {
    type: Number,
    default: Infinity
  },

  loading: {
    type: Boolean
  }
})

const listReference = useTemplateRef('list')

const onLoadMore = eventData => {
  listReference.value.loadMore(eventData)
}

const onResetSearchCriteria = async () => {
  if (!listReference.value) {
    await nextTick()
  }

  listReference.value.resetSearchCriteria()
}

const updateOkrData = async eventData => {
  if (!listReference.value) {
    await nextTick()
  }

  listReference.value.tryToUpdateOkrElements(eventData)
}

const expandAll = () => {
  listReference.value.onExpandAll()
}

const collapseAll = () => {
  listReference.value.onCollapseAll()
}

const tableReference = useTemplateRef('table')

const onDataRequest = expandAll => {
  tableReference.value.requestData(expandAll)
}

const store = useStore()

const workspaceId = ref(null)
const workspaces = computed(() => store.state.workspaces.workspaces)

const route = useRoute()

const handleWorkspaces = async () => {
  const { workspaceId: workspaceIdFromRoute } = route.params
  workspaceId.value = Number(workspaceIdFromRoute)
}

const wrapperReference = useTemplateRef('wrapper')

const wrapperStyles = ref({})

const setupExternalOffset = () => {
  wrapperStyles.value['--external-offset'] = `${wrapperReference.value.offsetTop}px`
}

const { t } = useI18n()

const useBulkAddState = () => {
  const elementsToAdd = computed(() => {
    return difference(state.value.checkedElements, state.value.alreadyAddedElements)
  })

  const elementsToAddCount = computed(() => elementsToAdd.value.length)

  const counter = computed(() => {
    if (!isFinite(state.value.limit)) return null

    return `${elementsToAddCount.value} / ${state.value.limit}`
  })

  const cacheKeyResolver = okrElement => {
    const { id, typeId, permissions = [] } = okrElement
    return [
      `elementId:${id}`,
      `typeId:${typeId}`,
      `permissions:${permissions.join('-')}`,
      `elementsToAdd:${elementsToAdd.value.join('-')}`
    ].join('|')
  }

  const getDisabledState = memoize((okrElement = {}) => {
    const disableState = {
      disabled: false,
      tooltip: null
    }

    if (!toValue(bulkAddState)) {
      return {
        ...disableState,
        disabled: true
      }
    }

    if (!currentUserCanReadObjective(okrElement)) {
      return {
        disabled: true,
        tooltip: t('bulk_add.disabled_by_permissions')
      }
    }

    const isDisabledByTypeId = toValue(bulkAddState).disabledElementsTypeIds.includes(
      okrElement[OKR_ELEMENT_ENTITY_KEYS.TYPE_ID]
    )

    if (isDisabledByTypeId) {
      return {
        disabled: true,
        tooltip: t('bulk.add.disabled_by_type')
      }
    }

    const isElementAlreadyAdded = toValue(bulkAddState).alreadyAddedElements.includes(
      okrElement[OKR_ELEMENT_ENTITY_KEYS.ID]
    )

    if (isElementAlreadyAdded) {
      return {
        disabled: true,
        tooltip: t('bulk.add.disabled_by_already_added')
      }
    }

    const { limit, elementsToAdd } = toValue(bulkAddState)

    const isLimitExceeded = elementsToAdd.length >= limit

    if (isLimitExceeded) {
      const disabled = !elementsToAdd.includes(okrElement[OKR_ELEMENT_ENTITY_KEYS.ID])
      const tooltip = disabled ? t('bulk_actions.disabled_by_count', limit) : null

      return {
        disabled,
        tooltip
      }
    }

    return disableState
  }, cacheKeyResolver)

  const state = ref({
    alreadyAddedElements: [],
    disabledElementsTypeIds: [OBJECTIVE_TYPES.TASK, OBJECTIVE_TYPES.WORK_ITEM],
    checkedElements: [],
    getDisabledState,
    elementsToAdd,
    limit: Infinity
  })

  const initBulkAddState = ({ alreadyAddedElements = [], limit = Infinity } = {}) => {
    state.value.alreadyAddedElements = [...alreadyAddedElements]
    state.value.checkedElements = [...alreadyAddedElements]
    state.value.limit = limit - alreadyAddedElements.length
  }

  const clearElementsToAdd = () => {
    state.value.checkedElements = [...state.value.alreadyAddedElements]
  }

  return {
    state,
    initBulkAddState,
    counter,
    elementsToAdd,
    elementsToAddCount,
    clearElementsToAdd
  }
}

const {
  state: bulkAddState,
  initBulkAddState,
  counter,
  elementsToAdd,
  elementsToAddCount,
  clearElementsToAdd
} = useBulkAddState()

provide(bulkAddStateInjectionKey, bulkAddState)

onMounted(() => {
  handleWorkspaces()
  initBulkAddState({
    alreadyAddedElements: props.alreadyAddedElements,
    limit: props.limit
  })
  setupExternalOffset()
})

const emit = defineEmits({
  'on-confirm': null
})

const onConfirm = () => {
  emit('on-confirm', {
    elementsToAdd: elementsToAdd.value,
    elementsData: listReference.value.getElementsDataByIds({
      elementIds: elementsToAdd.value
    })
  })
}
</script>

<style lang="scss" scoped>
.baet-Header {
  z-index: 10;
  position: sticky;
  top: var(--external-offset, 0);

  &:deep(.pc-PageContentHeader) {
    --padding-bottom: 24px;
    --padding-top: 12px;
  }
}

.baet-TableWrapper {
  padding: 0 var(--page-right-padding) 0 var(--page-left-padding);
}

.baet-Filter {
  max-width: 230px;
  --select-skeleton-left: 0px;
  --select-skeleton-top: 0px;

  &:deep(.si-SelectedItem) {
    span {
      line-height: 20px;
    }
  }
}

.baet-SelectedItemWrapper {
  display: flex;
  align-items: center;
}

.baet-Footer {
  z-index: 10;
  position: sticky;
  bottom: 0;
  padding: 20px var(--page-right-padding) 20px var(--page-left-padding);
  box-shadow: 0 -1px 0 $grey-2-next;
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: $white;
}

.baet-Footer_Counter {
  font-weight: fw('regular');
  font-size: $fs-12;
  line-height: 16px;
  color: $grey-1-next;
  padding-left: 4px;
}

.baet-Footer_Actions {
  display: inherit;
  gap: 12px;
  align-items: inherit;
  margin-left: auto;
}
</style>
